import DOMPurify from 'dompurify'

/**
 * Sanitiza HTML vindo de fontes externas (SDP, emails) antes de renderizar.
 * Usa DOMPurify com config restritiva: sem scripts, iframes, event handlers,
 * mas permite formatação rica (links, imagens, tabelas, listas, blockquotes).
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return ''
  const clean = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'a', 'b', 'i', 'em', 'strong', 'u', 's', 'sub', 'sup',
      'p', 'br', 'hr', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'q', 'cite', 'pre', 'code',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      'img', 'figure', 'figcaption',
      'small', 'mark', 'del', 'ins',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'title',
      'src', 'alt', 'width', 'height',
      'colspan', 'rowspan', 'align',
      'class', 'style',
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link', 'style'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
  })
  // Pós-processamento: imagens SDP-hosted (cookie-only) viram placeholder com link
  return rewriteSdpImages(clean)
}

/**
 * Imagens hospedadas no SDP (servlet legacy SDODAuthServlet) exigem cookie de
 * sessão Zoho — não funcionam fora do tenant SDP. Substituímos por placeholder
 * visual com link para o tenant SDP, onde o usuário (logado lá) consegue ver.
 *
 * Imagens externas (knak.io, gstatic, gravatar, etc) carregam direto.
 */
function rewriteSdpImages(html: string): string {
  if (!html) return ''
  const SDP_HOST = 'https://nai-it.sdpondemand.manageengine.com'

  // Pattern: <img ... src="/app/itdesk/..." ...> ou src="https://nai-it.sdpondemand.manageengine.com/...">
  const imgPattern = /<img\b[^>]*\bsrc=(["'])([^"']+)\1[^>]*>/gi

  return html.replace(imgPattern, (full, _q, src) => {
    const decoded = src.replace(/&amp;/g, '&')
    const isSdpRelative = decoded.startsWith('/app/itdesk/') || decoded.startsWith('/sdpapi/')
    const isSdpAbsolute = decoded.includes('sdpondemand.manageengine.com') || decoded.includes('manageengine.com/app/itdesk/')

    if (!isSdpRelative && !isSdpAbsolute) {
      // Imagem externa (logos email, knak.io, gstatic, gravatar) — adiciona referrerpolicy/loading e mantém
      let withAttrs = full
      if (!/loading=/i.test(withAttrs)) {
        withAttrs = withAttrs.replace(/<img\b/i, '<img loading="lazy"')
      }
      if (!/referrerpolicy=/i.test(withAttrs)) {
        withAttrs = withAttrs.replace(/<img\b/i, '<img referrerpolicy="no-referrer"')
      }
      return withAttrs
    }

    // Imagem SDP — substitui por placeholder com link
    const sdpUrl = isSdpAbsolute ? decoded : (SDP_HOST + decoded)
    return (
      `<a href="${escapeAttr(sdpUrl)}" target="_blank" rel="noopener noreferrer"` +
      ` class="nd-sdp-img-placeholder" title="Imagem hospedada no SDP — clique para ver no ServiceDesk Plus">` +
      `<span class="nd-sdp-img-icon">🖼️</span>` +
      `<span class="nd-sdp-img-text">imagem do SDP &nbsp;·&nbsp; abrir no ServiceDesk Plus</span>` +
      `</a>`
    )
  })
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Detecta se uma string parece HTML (contém tags) — útil para escolher
 * entre renderizar como HTML sanitizado ou como texto plano com whitespace-pre-wrap.
 */
export function looksLikeHtml(s: string | null | undefined): boolean {
  if (!s) return false
  return /<\/?[a-z][\s\S]*?>/i.test(s)
}
